import { p as proxyCustomElement, H, c as createEvent, h } from './index.js';
import { g as getValidityObject, c as checkValidity, r as reportValidity, s as setCustomValidity, a as setInputRef, b as getError, w as watchValue, d as watchError, e as watchWarning, f as renderFormField } from './p-Cg8AdZPK.js';
import { h as handleTabIndex } from './p-bL-8cS__.js';
import { d as defineCustomElement$5 } from './p-BbTg3Car.js';
import { d as defineCustomElement$4 } from './p-BlW2joRF.js';
import { d as defineCustomElement$3 } from './p-Dr61d_nc.js';
import { d as defineCustomElement$2 } from './p-_P4kPR3l.js';
import { d as defineCustomElement$1 } from './p-CdhcNtiX.js';

const toggleCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;width:fit-content}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.inner{align-items:center;cursor:pointer;display:flex;flex:none;flex-direction:row;gap:.25rem;height:1.25rem;width:fit-content}.label{font-family:Open Sans;font-size:.75rem;font-weight:700;letter-spacing:.03em;line-height:1rem;text-transform:uppercase;-webkit-user-select:none;user-select:none;width:24px}.box,.label{display:block}.box{background-color:var(--jw-theme-system-primary-default-bg-color);border-radius:624.9375rem;height:100%;overflow:hidden;position:relative;transition:background-color .2s ease;width:2.5rem}.handle{bottom:.125rem;left:.125rem;position:absolute;right:.125rem;top:.125rem;transition:transform .2s ease}.handle:after{background-color:var(--jw-theme-form-field-disabled-inverted-bg-color);border-radius:50%;content:\"\";height:1rem;left:0;position:absolute;top:0;width:1rem}:host([position=left]) .inner{flex-direction:row-reverse}:host([position=left]) .label{text-align:right}:host([checked]) .box{background-color:var(--jw-theme-system-primary-default-bg-color)}:host([checked]) .handle{transform:translateX(calc(100% - 1rem))}:host([checked]) .handle:after{background-color:var(--jw-theme-system-primary-default-color)}:host([checked]) .label-on{color:var(--jw-theme-system-surface-color);display:block}:host([checked]) .label-off{display:none}:host(:not([checked])) .box{background-color:var(--jw-theme-form-field-disabled-inverted-bg-color)}:host(:not([checked])) .handle:after{background-color:var(--jw-theme-form-field-disabled-inverted-color)}:host(:not([checked])) .label-on{display:none}:host(:not([checked])) .label-off{color:var(--jw-theme-surface-text-helper-color);display:block}:host([disabled]) .inner{cursor:not-allowed}:host([disabled]) .box{background-color:var(--jw-theme-form-field-disabled-inverted-bg-color)!important}:host([disabled]) .label{color:var(--jw-theme-surface-text-helper-color)}:host([disabled]) .handle:after{background-color:var(--jw-theme-form-field-disabled-color)}:host(:not([disabled]):focus){outline:none}:host(:not([disabled]):focus) .box{outline:var(--jw-theme-effects-focus-default-outline)}";

const WuiToggle = /*@__PURE__*/ proxyCustomElement(class WuiToggle extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.required = false;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        //////// FORM FIELD END ////////
        //////// TAB FOCUSABLE START ////////
        this._tabIndex = 0;
        /**
         * On/off label position
         */
        this.position = 'right';
        /**
         * Custom labels
         */
        this.labels = {
            on: 'ON',
            off: 'OFF',
        };
        this.handleChange = (e) => {
            e.stopPropagation();
            if (this.disabled) {
                return;
            }
            // The input element determines the behavior
            const target = this.inputElm;
            if (!target) {
                return;
            }
            this.dirty = true;
            this.checked = target.checked;
            this.checkValidity();
            this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
        };
    }
    _handleValueChange(newValue) {
        watchValue(this, newValue);
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    async _handleKeyDown(e) {
        if (this.disabled) {
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            // Forward event to hidden input
            this.inputElm?.click();
            e.preventDefault();
            e.stopPropagation();
            return true;
        }
    }
    componentWillRender() {
        handleTabIndex(this);
    }
    renderInner() {
        return (h("label", { class: "inner" }, h("div", { class: "box" }, h("input", { hidden: true, type: "checkbox", name: this.name, value: this.value, checked: this.checked, disabled: this.disabled, onInput: this.handleChange, required: !!this.required, onChange: (e) => {
                // We want to use the input event, not 'change'
                e.stopPropagation();
            }, ref: (el) => {
                this.inputElm = el;
            } }), h("div", { class: "handle" })), h("span", { class: "label label-on", "aria-hidden": this.checked ? 'false' : 'true' }, this.labels?.on ?? 'ON'), h("span", { class: "label label-off", "aria-hidden": this.checked ? 'true' : 'false' }, this.labels?.off ?? 'OFF')));
    }
    render() {
        return renderFormField(this, this.renderInner(), {
            host: {
                role: 'switch',
                'aria-checked': this.checked ? 'true' : 'false',
            },
        });
    }
    get elm() { return this; }
    static get watchers() { return {
        "value": ["_handleValueChange"],
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return toggleCss; }
}, [257, "wui-toggle", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16, "check-validity"],
        "reportValidity": [16, "report-validity"],
        "setCustomValidity": [16, "set-custom-validity"],
        "formField": [16, "form-field"],
        "name": [1],
        "checked": [1540],
        "disabled": [516],
        "position": [513],
        "labels": [16],
        "customValidation": [32],
        "showValidity": [32]
    }, [[0, "keydown", "_handleKeyDown"]], {
        "value": ["_handleValueChange"],
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-toggle", "wui-box", "wui-form-field", "wui-icon", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-toggle":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiToggle);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiToggle as W, defineCustomElement as d };
